import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import API_URL from '../utils/apiUrl';

// Use the page's own host (and matching ws/wss protocol) for WebSocket so it
// works on any deployed domain or raw IP without rebuilding.
const WS_URL = (typeof window !== 'undefined')
  ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
  : API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) return { unreadCount: 0, clearUnread: () => {}, requestPermission: () => {} };
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, userType, token, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const audioRef = useRef(null);
  const currentChatUserRef = useRef(null);

  // Only active for dating site users
  const isUser = isAuthenticated && userType === 'user';

  // Create notification sound
  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGczIjOD0teleEQ5XKm72aFrRjlpo8fWq3lPRWqew9GufllMZ5q+zau1bFJnlbfHsLpwV2eVtMK4vnBYZ5a0wbi+clhol7TAuMFyWWiYtcC4wnNZaJe1wLjCc1lol7XAuMJzWWiXtcC4wnNZaJi1wLjCc1pomLXAuMFzWWiYtcC5wXJZaJi1wLnBclpomLTAucFyWmiYtMC5wXJaaJi0wLnBcllomLXAucFyWWiYtcC4wnNZaJe1wLjCc1lol7XAuMJzWWiXtcC4wnNZaJi1wLjCc1lomLXAuMFzWWiYtcC5wXJZaJi1wLnBclpomLTAucFyWmiYtMC5wnFaaJm0v7nBcVppmrO+ucNwWWucs7y5xm5Wbp+yu7vIa1Nyo7C4vctmT3anrba8zmFLe6yrsr7SXEZ+sKqwv9VYQIO0qK7A2FJCiLenrMDXTkONuaaswdNKRZK8parBz0dFl7+jqcHOR0abwaKpwsxGR5/DoqnCy0VIo8Whp8PJRQ==');
    return () => { audioRef.current = null; };
  }, []);

  const playSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      setPermissionGranted(true);
      return;
    }
    if (Notification.permission !== 'denied') {
      const perm = await Notification.requestPermission();
      setPermissionGranted(perm === 'granted');
    }
  }, []);

  const showBrowserNotification = useCallback((title, body, onClick) => {
    if (!permissionGranted || document.hasFocus()) return;
    try {
      const n = new Notification(title, {
        body,
        icon: '❤️',
        tag: 'dating-msg',
        renotify: true
      });
      if (onClick) n.onclick = () => { window.focus(); onClick(); n.close(); };
      setTimeout(() => n.close(), 5000);
    } catch (e) { /* SW-only env */ }
  }, [permissionGranted]);

  // Set which chat user is currently being viewed (so we skip notifying for that)
  const setCurrentChatUser = useCallback((uid) => {
    currentChatUserRef.current = uid;
  }, []);

  const clearUnread = useCallback(() => setUnreadCount(0), []);

  // Fetch unread count on mount & periodically
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/messages/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const convs = res.data.conversations || [];
      const total = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnreadCount(total);
    } catch (e) { /* ignore */ }
  }, [token]);

  // WebSocket for real-time notifications
  useEffect(() => {
    if (!isUser || !user?.id) return;

    requestPermission();
    fetchUnreadCount();

    const connect = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/ws/chat/${user.id}?token=${token}`);
        ws.onopen = () => {};
        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            const msg = data.message;
            // Don't notify if sender is the user themselves
            if (msg.sender_id === user.id) return;
            // Don't notify if user is currently viewing that conversation
            if (currentChatUserRef.current === msg.sender_id) return;

            setUnreadCount(prev => prev + 1);
            playSound();
            showBrowserNotification(
              `New message from ${msg.sender_username || 'Someone'}`,
              msg.content?.substring(0, 80) || 'Sent an image',
              () => { window.location.href = `/chat/${msg.sender_id}`; }
            );
          }
        };
        ws.onclose = () => {
          reconnectRef.current = setTimeout(connect, 5000);
        };
        ws.onerror = () => {};
        wsRef.current = ws;
      } catch (e) { /* ignore */ }
    };

    connect();
    const pollInterval = setInterval(fetchUnreadCount, 15000);

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      clearInterval(pollInterval);
    };
  }, [isUser, user?.id, requestPermission, fetchUnreadCount, playSound, showBrowserNotification]);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      clearUnread,
      requestPermission,
      setCurrentChatUser
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
